import axios from 'axios';
import { getErrorMessage, extractErrorCode, extractErrorDetails } from '../../utils/errorHandler';

// The single axios instance for the whole frontend. STD-016 ratchet enforces
// that this is the only file importing axios for HTTP — domain modules import
// `api` from here, never axios directly.
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  timeout: 10000,
});

// Auth token provider function
let authTokenProvider: (() => string | null) | null = null;
// Auth logout callback for handling 401s
let authLogoutCallback: (() => void) | null = null;

/** Get the current auth token (for use outside axios, e.g. SSE fetch calls). */
export const getAuthToken = (): string | null => authTokenProvider?.() ?? null;

/** Get the API base URL. */
export const getApiBaseUrl = (): string => import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Configure API authentication
export const configureApiAuth = (getAuthToken: () => string | null, onLogout?: () => void) => {
  authTokenProvider = getAuthToken;
  authLogoutCallback = onLogout || null;
};

// Add request interceptor for Supabase JWT token injection
api.interceptors.request.use(
  (config) => {
    const token = authTokenProvider?.();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Log when no authentication token is available
      console.warn('API: No authentication token available for request to:', config.url);
    }

    return config;
  },
  (error) => {
    console.error('API: Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Extract and attach user-friendly error message to the error object
    const userMessage = getErrorMessage(error);
    const errorCode = extractErrorCode(error);
    const errorDetails = extractErrorDetails(error);

    // Attach formatted message for easy access in components
    (error as Record<string, unknown>).userMessage = userMessage;

    // Log error with code and details for debugging
    console.error('API: Response error:', error);
    if (errorCode) {
      console.error('API: Error code:', errorCode);
    }
    if (errorDetails) {
      console.error('API: Error details:', errorDetails);
    }

    // Handle specific HTTP status codes
    const status = error.response?.status;

    if (status === 401) {
      console.error('API: Authentication failed (401)');
      // Trigger logout if we have a logout callback
      if (authLogoutCallback) {
        console.log('🔐 API: Triggering logout due to 401 error');
        authLogoutCallback();
      }
    } else if (status === 403) {
      console.error('API: Permission denied (403)');
    } else if (status === 404) {
      console.error('API: Resource not found (404)');
    } else if (status === 409) {
      console.error('API: Conflict (409)');
    } else if (status === 422) {
      console.error('API: Validation error (422)');
    } else if (status === 500) {
      console.error('API: Server error (500)');
    } else if (status === 503) {
      console.error('API: Service unavailable (503)');
    }

    return Promise.reject(error);
  }
);

// There is deliberately NO browser→S3 upload helper here. `uploadFileToPresignedUrl`
// used to PUT installers straight at a presigned S3 URL — the product's only
// cross-origin XHR, and one that could never work: the signed `Content-Type`
// makes it preflight, and the bucket has no CORS configuration. Every upload
// now goes SPA → FastAPI → boto3, so the trap is structurally unreachable
// rather than merely unused. See `docs/GOTCHAS.md` G-7 before adding one back.

export { api };
